/**
 * Validation utilities matching backend IPOSerializer validations
 */

export interface ValidationError {
  field: string;
  message: string;
}

const _norm = (s: string | null | undefined): string => {
  return (s ?? "").trim();
};

/**
 * Validate company name
 */
export const validateCompanyName = (
  value: string | null | undefined
): string | null => {
  const normalized = _norm(value);
  if (!normalized) {
    return "Company name is required.";
  }
  if (normalized.length < 2) {
    return "Company name must be at least 2 characters.";
  }
  if (normalized.length > 255) {
    return "Company name must be 255 characters or less.";
  }
  return null;
};

/**
 * Validate brand short name
 */
export const validateBrandShortName = (
  value: string | null | undefined
): string | null => {
  const normalized = _norm(value);
  if (!normalized) {
    return "Brand short name is required.";
  }
  if (normalized.length < 2) {
    return "Brand short name must be at least 2 characters.";
  }
  if (normalized.length > 100) {
    return "Brand short name must be 100 characters or less.";
  }
  return null;
};

/**
 * Validate NSE symbol
 */
export const validateNSESymbol = (
  value: string | null | undefined
): string | null => {
  if (!value) {
    return null;
  }
  const normalized = _norm(value);
  if (normalized && normalized.length > 50) {
    return "NSE symbol must be 50 characters or less.";
  }
  return null;
};

/**
 * Validate BSE symbol (must be digits only)
 */
export const validateBSESymbol = (
  value: string | null | undefined
): string | null => {
  if (!value) {
    return null;
  }
  const normalized = _norm(value);
  if (normalized) {
    if (!/^[0-9]+$/.test(normalized)) {
      return "BSE symbol must contain only digits.";
    }
    if (normalized.length > 50) {
      return "BSE symbol must be 50 characters or less.";
    }
  }
  return null;
};

/**
 * Validate phone number
 */
export const validatePhone = (
  value: string | null | undefined
): string | null => {
  if (!value) {
    return null;
  }
  const normalized = _norm(value);
  if (normalized) {
    if (!/^[\d\s\-+()]+$/.test(normalized)) {
      return "Invalid phone number format.";
    }
    if (normalized.length > 20) {
      return "Phone number must be 20 characters or less.";
    }
  }
  return null;
};

/**
 * Validate founded year
 */
export const validateFoundedYear = (
  value: number | string | null | undefined
): string | null => {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  const numValue = typeof value === "string" ? parseInt(value, 10) : value;
  if (isNaN(numValue)) {
    return null;
  }
  if (numValue < 1800) {
    return "Founded year must be 1800 or later.";
  }
  const currentYear = new Date().getFullYear();
  if (numValue > currentYear) {
    return `Founded year cannot be greater than ${currentYear}.`;
  }
  return null;
};

/**
 * Validate percentage field (0-100)
 */
export const validatePercentage = (
  value: number | string | null | undefined,
  fieldName: string
): string | null => {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  const numValue = typeof value === "string" ? parseFloat(value) : value;
  if (isNaN(numValue)) {
    return null;
  }
  if (numValue < 0 || numValue > 100) {
    return `${fieldName} must be between 0 and 100.`;
  }
  return null;
};

/**
 * Validate positive number
 */
export const validatePositiveNumber = (
  value: number | string | null | undefined,
  fieldName: string,
  min: number = 0
): string | null => {
  if (value === null || value === undefined || value === "") {
    return `${fieldName} is required.`;
  }
  const numValue = typeof value === "string" ? parseFloat(value) : value;
  if (isNaN(numValue)) {
    return `${fieldName} must be a valid number.`;
  }
  if (numValue < min) {
    return `${fieldName} must be ${min} or greater.`;
  }
  return null;
};

/**
 * Validate integer
 */
export const validateInteger = (
  value: number | string | null | undefined,
  fieldName: string,
  min: number = 0
): string | null => {
  if (value === null || value === undefined || value === "") {
    return `${fieldName} is required.`;
  }
  const numValue = typeof value === "string" ? parseInt(value, 10) : value;
  if (isNaN(numValue) || !Number.isInteger(numValue)) {
    return `${fieldName} must be a valid integer.`;
  }
  if (numValue < min) {
    return `${fieldName} must be ${min} or greater.`;
  }
  return null;
};

/**
 * Validate email
 */
export const validateEmail = (
  value: string | null | undefined
): string | null => {
  if (!value) {
    return null;
  }
  const normalized = _norm(value);
  if (normalized) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(normalized)) {
      return "Invalid email format.";
    }
  }
  return null;
};

/**
 * Validate URL (accepts full URLs or domain-only like "gspcrop.com")
 */
export const validateURL = (
  value: string | null | undefined
): string | null => {
  if (!value) {
    return null;
  }
  const normalized = _norm(value);
  if (normalized) {
    try {
      // Accept domain-only input (e.g. "gspcrop.com") by trying with https://
      const toValidate = /^https?:\/\//i.test(normalized)
        ? normalized
        : `https://${normalized}`;
      new URL(toValidate);
    } catch {
      return "Invalid URL format.";
    }
  }
  return null;
};

/**
 * Validate date range
 */
export const validateDateRange = (
  startDate: string | null | undefined,
  endDate: string | null | undefined,
  startField: string,
  endField: string
): string | null => {
  if (!startDate || !endDate) {
    return null;
  }
  const start = new Date(startDate);
  const end = new Date(endDate);
  if (isNaN(start.getTime()) || isNaN(end.getTime())) {
    return null;
  }
  if (start > end) {
    return `${endField} must be after or equal to ${startField}.`;
  }
  return null;
};

/**
 * Comprehensive form validation matching backend IPOSerializer.validate()
 */
export const validateIPOForm = (
  formData: any,
  dateFields: any
): ValidationError[] => {
  const errors: ValidationError[] = [];

  // Required string fields
  const companyNameError = validateCompanyName(formData.company_name);
  if (companyNameError) {
    errors.push({ field: "company_name", message: companyNameError });
  }

  const brandShortNameError = validateBrandShortName(
    formData.company_short_name
  );
  if (brandShortNameError) {
    errors.push({ field: "company_short_name", message: brandShortNameError });
  }

  // Optional string fields
  const nseSymbolError = validateNSESymbol(formData.nse_symbol);
  if (nseSymbolError) {
    errors.push({ field: "nse_symbol", message: nseSymbolError });
  }

  const bseSymbolError = validateBSESymbol(formData.bse_symbol);
  if (bseSymbolError) {
    errors.push({ field: "bse_symbol", message: bseSymbolError });
  }

  const companyPhoneError = validatePhone(formData.company_phone);
  if (companyPhoneError) {
    errors.push({ field: "company_phone", message: companyPhoneError });
  }

  const registrarPhoneError = validatePhone(formData.registrar_phone);
  if (registrarPhoneError) {
    errors.push({ field: "registrar_phone", message: registrarPhoneError });
  }

  // Email validations
  const companyEmailError = validateEmail(formData.company_email);
  if (companyEmailError) {
    errors.push({ field: "company_email", message: companyEmailError });
  }

  const registrarEmailError = validateEmail(formData.registrar_email);
  if (registrarEmailError) {
    errors.push({ field: "registrar_email", message: registrarEmailError });
  }

  // URL validations
  if (formData.website) {
    const websiteError = validateURL(formData.website);
    if (websiteError) {
      errors.push({ field: "website", message: websiteError });
    }
  }

  if (formData.logo_url) {
    const logoError = validateURL(formData.logo_url);
    if (logoError) {
      errors.push({ field: "logo_url", message: logoError });
    }
  }

  if (formData.rhp_url) {
    const rhpError = validateURL(formData.rhp_url);
    if (rhpError) {
      errors.push({ field: "rhp_url", message: rhpError });
    }
  }

  if (formData.drhp_url) {
    const drhpError = validateURL(formData.drhp_url);
    if (drhpError) {
      errors.push({ field: "drhp_url", message: drhpError });
    }
  }

  if (formData.registrar_website) {
    const registrarWebsiteError = validateURL(formData.registrar_website);
    if (registrarWebsiteError) {
      errors.push({
        field: "registrar_website",
        message: registrarWebsiteError,
      });
    }
  }

  // Number validations
  const foundedYearError = validateFoundedYear(formData.founded_year);
  if (foundedYearError) {
    errors.push({ field: "founded_year", message: foundedYearError });
  }

  const priceMinError = validatePositiveNumber(
    formData.price_min,
    "Price band minimum"
  );
  if (priceMinError) {
    errors.push({ field: "price_min", message: priceMinError });
  }

  const priceMaxError = validatePositiveNumber(
    formData.price_max,
    "Price band maximum"
  );
  if (priceMaxError) {
    errors.push({ field: "price_max", message: priceMaxError });
  }

  // Price band validation
  if (formData.price_min && formData.price_max) {
    const priceMin = parseFloat(formData.price_min);
    const priceMax = parseFloat(formData.price_max);
    if (!isNaN(priceMin) && !isNaN(priceMax) && priceMin > priceMax) {
      errors.push({
        field: "price_max",
        message:
          "Maximum price band must be greater than or equal to minimum price band.",
      });
    }
  }

  const lotSizeError = validateInteger(formData.lot_size, "Lot size", 1);
  if (lotSizeError) {
    errors.push({ field: "lot_size", message: lotSizeError });
  }

  const faceValueError = validatePositiveNumber(
    formData.face_value,
    "Face value"
  );
  if (faceValueError) {
    errors.push({ field: "face_value", message: faceValueError });
  }

  const totalIssueSizeError = validatePositiveNumber(
    formData.issue_size,
    "Total issue size"
  );
  if (totalIssueSizeError) {
    errors.push({ field: "issue_size", message: totalIssueSizeError });
  }

  // Share validations
  const shareFields = [
    "qib_shares_offered",
    "nii_shares_offered",
    "retail_shares_offered",
    "anchor_shares_offered",
    "employee_shares_offered",
    "market_maker_shares_offered",
    "pre_issue_shares",
    "post_issue_shares",
  ];

  shareFields.forEach(field => {
    if (
      formData[field] !== "" &&
      formData[field] !== null &&
      formData[field] !== undefined
    ) {
      const error = validateInteger(
        formData[field],
        field.replace(/_/g, " "),
        0
      );
      if (error) {
        errors.push({ field, message: error });
      }
    }
  });

  // Post-issue shares validation
  if (formData.pre_issue_shares && formData.post_issue_shares) {
    const preIssue = parseInt(formData.pre_issue_shares);
    const postIssue = parseInt(formData.post_issue_shares);
    if (!isNaN(preIssue) && !isNaN(postIssue) && postIssue < preIssue) {
      errors.push({
        field: "post_issue_shares",
        message:
          "Post-issue shares must be greater than or equal to pre-issue shares.",
      });
    }
  }

  // Percentage validations
  const percentageFields = [
    { field: "roe", name: "ROE percentage" },
    { field: "roce", name: "ROCE percentage" },
    { field: "pat_margin", name: "PAT margin percentage" },
    { field: "subscription_retail", name: "Retail portion percentage" },
    { field: "reservations_qib", name: "QIB portion percentage" },
    { field: "reservations_nii", name: "NII portion percentage" },
    {
      field: "reservations_retail",
      name: "Retail portion allocation percentage",
    },
    { field: "reservations_employee", name: "Employee portion percentage" },
    { field: "pre_issue_holding", name: "Pre-issue holding percentage" },
    { field: "post_issue_holding", name: "Post-issue holding percentage" },
  ];

  percentageFields.forEach(({ field, name }) => {
    if (
      formData[field] !== "" &&
      formData[field] !== null &&
      formData[field] !== undefined
    ) {
      const error = validatePercentage(formData[field], name);
      if (error) {
        errors.push({ field, message: error });
      }
    }
  });

  // Date validations
  if (dateFields.bidStartDate && dateFields.bidEndDate) {
    const dateError = validateDateRange(
      dateFields.bidStartDate,
      dateFields.bidEndDate,
      "Opening date",
      "Closing date"
    );
    if (dateError) {
      errors.push({ field: "bidEndDate", message: dateError });
    }
  }

  if (dateFields.bidEndDate && dateFields.listingDate) {
    const dateError = validateDateRange(
      dateFields.bidEndDate,
      dateFields.listingDate,
      "Closing date",
      "Listing date"
    );
    if (dateError) {
      errors.push({ field: "listingDate", message: dateError });
    }
  }

  if (dateFields.anchorBiddingDate && dateFields.bidStartDate) {
    const dateError = validateDateRange(
      dateFields.anchorBiddingDate,
      dateFields.bidStartDate,
      "Anchor bidding date",
      "Opening date"
    );
    if (dateError) {
      errors.push({ field: "anchorBiddingDate", message: dateError });
    }
  }

  // Issue size validation
  if (formData.fresh_issue && formData.ofs && formData.issue_size) {
    const freshIssue = parseFloat(formData.fresh_issue);
    const ofs = parseFloat(formData.ofs);
    const totalIssueSize = parseFloat(formData.issue_size);
    if (!isNaN(freshIssue) && !isNaN(ofs) && !isNaN(totalIssueSize)) {
      const expectedTotal = freshIssue + ofs;
      if (Math.abs(totalIssueSize - expectedTotal) > 0.01) {
        errors.push({
          field: "issue_size",
          message: `Total issue size should equal fresh issue + offer for sale. Expected: ${expectedTotal}, Provided: ${totalIssueSize}.`,
        });
      }
    }
  }

  // Allocation fields validation (required fields)
  const retailMinLotError = validateInteger(
    formData.retail_min_lot,
    "Retail minimum lot",
    1
  );
  if (retailMinLotError) {
    errors.push({ field: "retail_min_lot", message: retailMinLotError });
  }

  const retailMinSharesError = validateInteger(
    formData.retail_min_shares,
    "Retail minimum shares",
    1
  );
  if (retailMinSharesError) {
    errors.push({ field: "retail_min_shares", message: retailMinSharesError });
  }

  const sniiLotsError = validateInteger(formData.snii_lots, "sNII lots", 1);
  if (sniiLotsError) {
    errors.push({ field: "snii_lots", message: sniiLotsError });
  }

  const sniiMinInvestmentError = validatePositiveNumber(
    formData.snii_min_investment,
    "sNII minimum investment"
  );
  if (sniiMinInvestmentError) {
    errors.push({
      field: "snii_min_investment",
      message: sniiMinInvestmentError,
    });
  }

  const sniiMinSharesError = validateInteger(
    formData.snii_min_shares,
    "sNII minimum shares",
    1
  );
  if (sniiMinSharesError) {
    errors.push({ field: "snii_min_shares", message: sniiMinSharesError });
  }

  const bniiLotsError = validateInteger(formData.bnii_lots, "bNII lots", 1);
  if (bniiLotsError) {
    errors.push({ field: "bnii_lots", message: bniiLotsError });
  }

  const bniiMinInvestmentError = validatePositiveNumber(
    formData.bnii_min_investment,
    "bNII minimum investment"
  );
  if (bniiMinInvestmentError) {
    errors.push({
      field: "bnii_min_investment",
      message: bniiMinInvestmentError,
    });
  }

  const bniiMinSharesError = validateInteger(
    formData.bnii_min_shares,
    "bNII minimum shares",
    1
  );
  if (bniiMinSharesError) {
    errors.push({ field: "bnii_min_shares", message: bniiMinSharesError });
  }

  return errors;
};

/**
 * Get field type (string or number) based on backend serializer
 */
export const getFieldType = (fieldName: string): "string" | "number" => {
  const numberFields = [
    "founded_year",
    "employees",
    "price_min",
    "price_max",
    "lot_size",
    "face_value",
    "issue_size",
    "fresh_issue",
    "ofs",
    "pre_issue_shares",
    "post_issue_shares",
    "pre_issue_holding",
    "post_issue_holding",
    "qib_shares_offered",
    "nii_shares_offered",
    "retail_shares_offered",
    "anchor_shares_offered",
    "employee_shares_offered",
    "market_maker_shares_offered",
    "employee_discount",
    "pe_ratio",
    "pbv_ratio",
    "roe",
    "roce",
    "pat_margin",
    "debt_equity",
    "eps_pre",
    "gmp",
    "expected_listing",
    "listing_gain",
    "subscription_total",
    "subscription_retail",
    "reservations_qib",
    "reservations_nii",
    "reservations_retail",
    "reservations_employee",
    "retail_min_lot",
    "retail_min_shares",
    "snii_lots",
    "snii_min_investment",
    "snii_min_shares",
    "bnii_lots",
    "bnii_min_investment",
    "bnii_min_shares",
  ];

  return numberFields.includes(fieldName) ? "number" : "string";
};
